import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle, BadgePercent, Building2, Car, Clock3, CreditCard, Handshake, LayoutDashboard,
  Loader2, MessageSquare, Megaphone, RefreshCcw, Tag, Undo2, Users,
} from 'lucide-react';
import api from '@/lib/axios';

const PERIODOS = [['hoje', 'Hoje'], ['7d', 'Últimos 7 dias'], ['30d', 'Últimos 30 dias'],
  ['mes', 'Mês atual'], ['tudo', 'Tudo'], ['custom', 'Personalizado']];

const Card = ({ icon: Icon, label, value, hint, tone = 'slate', onClick, testId }) => {
  const tones = {
    slate: 'border-slate-200 bg-white text-slate-900',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-900',
    amber: 'border-amber-200 bg-amber-50 text-amber-900',
    rose: 'border-rose-200 bg-rose-50 text-rose-900',
    cyan: 'border-cyan-200 bg-cyan-50 text-cyan-900',
  };
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className={`flex flex-col items-start rounded-2xl border p-3 text-left transition hover:-translate-y-0.5 hover:shadow-md ${tones[tone]}`}>
      <Icon className="mb-1.5 h-4 w-4 opacity-70" />
      <span className="text-[10px] font-bold uppercase tracking-wide opacity-70">{label}</span>
      <span className="text-2xl font-black leading-tight">{value}</span>
      {hint && <span className="mt-0.5 text-[11px] opacity-70">{hint}</span>}
    </button>
  );
};

export default function MarketplaceOverviewPanel({ onGoTo = () => {} }) {
  const [periodo, setPeriodo] = useState('30d');
  const [custom, setCustom] = useState({ de: '', ate: '' });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const params = new URLSearchParams({ periodo });
      if (periodo === 'custom') {
        if (!custom.de || !custom.ate) { setLoading(false); return; }
        params.set('de', new Date(custom.de).toISOString());
        params.set('ate', new Date(`${custom.ate}T23:59:59`).toISOString());
      }
      const res = await api.get(`/admin/marketplace/overview?${params.toString()}`);
      setData(res.data);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Falha a carregar a visão geral.');
    } finally { setLoading(false); }
  }, [periodo, custom]);

  useEffect(() => { load(); }, [load]);

  const a = data?.anuncios || {}; const e = data?.entidades || {};
  const c = data?.comunicacao || {}; const k = data?.comercial || {}; const p = data?.publicidade || {};

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="admin-overview-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="inline-flex items-center gap-2 text-base font-bold text-slate-900 md:text-lg">
          <LayoutDashboard className="h-4 w-4 text-emerald-600" /> Visão geral
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          <select value={periodo} onChange={(ev) => setPeriodo(ev.target.value)}
            className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs" data-testid="admin-overview-period">
            {PERIODOS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          {periodo === 'custom' && (
            <>
              <input type="date" value={custom.de} onChange={(ev) => setCustom((s) => ({ ...s, de: ev.target.value }))}
                className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs" data-testid="admin-overview-from" />
              <input type="date" value={custom.ate} onChange={(ev) => setCustom((s) => ({ ...s, ate: ev.target.value }))}
                className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs" data-testid="admin-overview-to" />
            </>
          )}
          <button type="button" onClick={load} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-semibold text-slate-600" data-testid="admin-overview-reload">
            <RefreshCcw className="h-3.5 w-3.5" /> Atualizar
          </button>
        </div>
      </div>

      {err && <p className="mt-2 text-xs font-semibold text-red-600" data-testid="admin-overview-error">{err}</p>}
      {loading && <p className="mt-4 flex items-center gap-2 text-xs text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> A carregar...</p>}

      {!loading && data && (
        <>
          {data.sem_dados && (
            <p className="mt-3 rounded-xl bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-600" data-testid="admin-overview-empty">
              Sem dados no período selecionado.
            </p>
          )}

          <p className="mt-4 text-[11px] font-bold uppercase tracking-wide text-slate-400">Anúncios</p>
          <div className="mt-2 grid gap-2 sm:grid-cols-3 lg:grid-cols-5">
            <Card icon={Car} tone="emerald" label="Ativos" value={a.ativos ?? 0} onClick={() => onGoTo('anuncios-aluguer')} testId="ov-ativos" />
            <Card icon={Clock3} tone="amber" label="Pendentes" value={a.pendentes ?? 0} hint="Aguardam validação" onClick={() => onGoTo('anuncios-aluguer')} testId="ov-pendentes" />
            <Card icon={AlertTriangle} tone="amber" label="A terminar" value={a.a_terminar ?? 0} hint="Próximos 7 dias" onClick={() => onGoTo('anuncios-aluguer')} testId="ov-terminar" />
            <Card icon={Clock3} label="Expirados" value={a.expirados ?? 0} onClick={() => onGoTo('anuncios-aluguer')} testId="ov-expirados" />
            <Card icon={Car} label="Novos no período" value={a.novos_no_periodo ?? 0} testId="ov-novos" />
            <Card icon={Car} label="Aluguer" value={a.aluguer ?? 0} onClick={() => onGoTo('anuncios-aluguer')} testId="ov-aluguer" />
            <Card icon={Tag} label="Venda" value={a.venda ?? 0} onClick={() => onGoTo('anuncios-venda')} testId="ov-venda" />
            <Card icon={Users} label="Motoristas" value={a.motoristas ?? 0} onClick={() => onGoTo('motoristas')} testId="ov-motoristas" />
            <Card icon={Car} label="Alugadas" value={a.alugadas ?? 0} testId="ov-alugadas" />
            <Card icon={Tag} label="Vendidas" value={a.vendidas ?? 0} testId="ov-vendidas" />
          </div>

          <p className="mt-5 text-[11px] font-bold uppercase tracking-wide text-slate-400">Entidades e comunicação</p>
          <div className="mt-2 grid gap-2 sm:grid-cols-3 lg:grid-cols-5">
            <Card icon={Building2} label="Stands" value={e.stands ?? 0} onClick={() => onGoTo('stands')} testId="ov-stands" />
            <Card icon={Handshake} label="Parceiros" value={e.parceiros ?? 0} onClick={() => onGoTo('parceiros')} testId="ov-parceiros" />
            <Card icon={Users} label="Associações" value={e.associacoes ?? 0} onClick={() => onGoTo('associacoes')} testId="ov-associacoes" />
            <Card icon={Clock3} tone="amber" label="Perfis pendentes" value={e.parceiros_pendentes ?? 0} onClick={() => onGoTo('parceiros')} testId="ov-parceiros-pendentes" />
            <Card icon={BadgePercent} label="Destaques ativos" value={e.destaques_ativos ?? 0} onClick={() => onGoTo('destaques')} testId="ov-destaques" />
            <Card icon={MessageSquare} tone="cyan" label="Conversas abertas" value={c.conversas_abertas ?? 0} onClick={() => onGoTo('conversas')} testId="ov-conversas" />
            <Card icon={AlertTriangle} tone="rose" label="Denúncias pendentes" value={c.denuncias_pendentes ?? 0} onClick={() => onGoTo('denuncias')} testId="ov-denuncias" />
          </div>

          <p className="mt-5 text-[11px] font-bold uppercase tracking-wide text-slate-400">Comercial</p>
          <div className="mt-2 grid gap-2 sm:grid-cols-3 lg:grid-cols-5">
            <Card icon={CreditCard} tone="amber" label="Encomendas pendentes" value={k.encomendas_pendentes ?? 0} onClick={() => onGoTo('encomendas')} testId="ov-encomendas" />
            <Card icon={CreditCard} tone="emerald" label="Pagamentos confirmados" value={k.pagamentos_confirmados ?? 0} onClick={() => onGoTo('pagamentos')} testId="ov-pagos" />
            <Card icon={CreditCard} tone="rose" label="Pagamentos falhados" value={k.pagamentos_falhados ?? 0} onClick={() => onGoTo('pagamentos')} testId="ov-falhados" />
            <Card icon={Tag} tone="emerald" label="Valor comercial" value={`${(k.valor_comercial ?? 0).toFixed(2)} ${k.moeda || 'EUR'}`} testId="ov-valor" />
            <Card icon={Undo2} label="Reembolsos" value={k.reembolsos ?? 0} hint={`${(k.valor_reembolsado ?? 0).toFixed(2)} ${k.moeda || 'EUR'}`} onClick={() => onGoTo('reembolsos')} testId="ov-reembolsos" />
            <Card icon={BadgePercent} label="Promoções ativas" value={k.campanhas_promocionais_ativas ?? 0} onClick={() => onGoTo('promocoes')} testId="ov-promocoes" />
            <Card icon={Tag} label="Códigos ativos" value={k.codigos_ativos ?? 0} onClick={() => onGoTo('promocoes')} testId="ov-codigos" />
            <Card icon={Handshake} label="Parcerias divulgação" value={k.parcerias_divulgacao ?? 0} onClick={() => onGoTo('parcerias')} testId="ov-parcerias" />
          </div>

          <p className="mt-5 text-[11px] font-bold uppercase tracking-wide text-slate-400">Publicidade</p>
          <div className="mt-2 grid gap-2 sm:grid-cols-3 lg:grid-cols-4">
            <Card icon={Megaphone} label="Campanhas ativas" value={p.campanhas_ativas ?? 0} onClick={() => onGoTo('publicidade')} testId="ov-ads-campanhas" />
            <Card icon={Megaphone} label="Impressões" value={p.impressoes ?? 0} testId="ov-ads-impressoes" />
            <Card icon={Megaphone} label="Cliques" value={p.cliques ?? 0} testId="ov-ads-cliques" />
            <Card icon={Megaphone} label="CTR" value={`${p.ctr ?? 0}%`} testId="ov-ads-ctr" />
          </div>
        </>
      )}
    </section>
  );
}
